/**
 * MKS-349 follow-up (2026-10-18) — "Mark all invoices issued before <date> as
 * paid", on /invoices.
 *
 * Step one of the accounting back-fill rollout: every historical invoice is
 * marked paid in one action, and the import of the accounting system's list of
 * UNPAID invoices then flips the few that are not.
 *
 * The shape of the control is the whole safety story, so it is deliberate:
 *
 *   - the panel is COLLAPSED until asked for, because it is a once-a-year
 *     action sitting on a page finance uses every day;
 *   - the date gets a preview (count + total) and the action button is
 *     disabled until that preview has been seen — nobody marks several
 *     hundred invoices paid without first reading how many;
 *   - the batch stays undoable after a reload, because the undo reads the
 *     batches back from the server rather than from component state.
 */
import { useState } from 'react';
import { Button } from '../common/Button';
import { DismissibleErrorBanner } from '../common/DismissibleErrorBanner';
import { DateField } from '../ui/DateField';
import {
  useApplyBulkPaid,
  useBulkPaidBatches,
  useBulkPaidPreview,
  useUndoBulkPaid,
} from '../../hooks/useInvoiceBulkPaid';
import { formatCurrency, formatDisplayDate, formatDisplayDateTime } from '../../utils/format';
import type { BulkPaidResult, BulkPaidUndoResult } from '../../services/api/invoiceBulkPaid';

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function BulkMarkPaidPanel() {
  const [open, setOpen] = useState(false);
  const [issuedBefore, setIssuedBefore] = useState('');
  /** The date the visible preview belongs to — the apply is gated on this. */
  const [previewedFor, setPreviewedFor] = useState('');
  const [applied, setApplied] = useState<BulkPaidResult | null>(null);
  const [undone, setUndone] = useState<BulkPaidUndoResult | null>(null);

  const preview = useBulkPaidPreview(previewedFor, open);
  const batches = useBulkPaidBatches(open);
  const applyMutation = useApplyBulkPaid();
  const undoMutation = useUndoBulkPaid();

  const previewData = previewedFor === issuedBefore ? preview.data?.data : undefined;
  const canApply =
    previewData !== undefined && previewData.count > 0 && !previewData.tooMany;

  const handleApply = () => {
    if (!canApply) return;
    setUndone(null);
    applyMutation.mutate(issuedBefore, {
      onSuccess: (response) => {
        setApplied(response.data);
        // The preview is now stale by construction: everything it counted is
        // paid. Drop it so the button cannot be pressed twice on one reading.
        setPreviewedFor('');
      },
    });
  };

  const handleUndo = (batchId: string) => {
    setApplied(null);
    undoMutation.mutate(batchId, { onSuccess: (response) => setUndone(response.data) });
  };

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Bulk mark as paid
      </Button>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Mark older invoices as paid</h2>
          <p className="mt-1 text-xs text-gray-500">
            Marks every <strong>unpaid</strong> invoice issued before the chosen day. The payment
            date is the invoice&apos;s due date, or its period end, or the day it was created —
            never a day in the future. Already-paid invoices are left alone, and the whole batch can
            be undone.
          </p>
        </div>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>

      {applyMutation.isError && (
        <div className="mt-3">
          <DismissibleErrorBanner
            message={errorMessage(applyMutation.error, 'Marking the invoices failed.')}
            onDismiss={() => applyMutation.reset()}
          />
        </div>
      )}
      {undoMutation.isError && (
        <div className="mt-3">
          <DismissibleErrorBanner
            message={errorMessage(undoMutation.error, 'Undoing the batch failed.')}
            onDismiss={() => undoMutation.reset()}
          />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label
            htmlFor="bulk-paid-before"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Issued before
          </label>
          <DateField
            id="bulk-paid-before"
            aria-label="Issued before"
            value={issuedBefore}
            onChange={(next) => {
              setIssuedBefore(next);
              // A changed date invalidates the reading the button was gated
              // on, so the operator has to preview again.
              setPreviewedFor('');
              setApplied(null);
            }}
            className="rounded-md border-gray-300 text-sm shadow-sm"
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => setPreviewedFor(issuedBefore)}
          disabled={issuedBefore === ''}
          isLoading={preview.isFetching}
        >
          Preview
        </Button>
        <Button onClick={handleApply} disabled={!canApply} isLoading={applyMutation.isPending}>
          Mark as paid
        </Button>
      </div>

      {previewData && (
        <p className="mt-3 text-sm text-gray-700">
          <strong>{previewData.count}</strong> unpaid invoice
          {previewData.count === 1 ? '' : 's'} issued before{' '}
          {formatDisplayDate(previewData.issuedBefore)}, totalling{' '}
          <strong>{formatCurrency(previewData.totalAmount, false)}</strong>.
          {previewData.count === 0 && ' Nothing to mark.'}
          {previewData.tooMany && (
            <span className="block text-amber-700">
              That is over the limit of {previewData.maxAllowed} for one action — choose an earlier
              date.
            </span>
          )}
        </p>
      )}

      {applied && (
        <p className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-900">
          {applied.marked} invoice{applied.marked === 1 ? '' : 's'} marked as paid.
        </p>
      )}
      {undone && (
        <p className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-900">
          {undone.reverted} invoice{undone.reverted === 1 ? '' : 's'} set back to unpaid
          {undone.skipped > 0 && `, ${undone.skipped} left alone (changed since the batch ran)`}.
        </p>
      )}

      {(batches.data?.data.length ?? 0) > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500">
            Recent batches
          </h3>
          <ul className="mt-1 space-y-1 text-sm">
            {batches.data?.data.map((batch) => (
              <li key={batch.batchId} className="flex items-center justify-between gap-3">
                <span className="text-gray-700">
                  {formatDisplayDateTime(batch.at)} · {batch.marked} invoice
                  {batch.marked === 1 ? '' : 's'}
                  {batch.issuedBefore && ` issued before ${formatDisplayDate(batch.issuedBefore)}`}
                  {batch.undone > 0 && ` · ${batch.undone} already undone`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUndo(batch.batchId)}
                  isLoading={undoMutation.isPending && undoMutation.variables === batch.batchId}
                >
                  Undo
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
